// Cost Dashboard Component - Real-time budget tracking
// frontend/src/components/CostDashboard.tsx

import React, { useState, useEffect } from 'react';
import { agentManager } from '../services/agentManager';

interface CostDashboardProps {
    budget: number;
}

export const CostDashboard: React.FC<CostDashboardProps> = ({ budget }) => {
    const [status, setStatus] = useState<any>(null);


    useEffect(() => {
        // Initialize agents if not already done
        if (!agentManager.isInitialized()) {
            agentManager.initializeForEpoch(budget);

        }

        // Update status once on mount
        updateStatus();
    }, [budget]);

    const updateStatus = () => {
        try {
            if (agentManager.isInitialized()) {
                const costAgent = agentManager.getCostAgent();
                setStatus(costAgent.getStatus());
            }
        } catch (error) {
            console.error('[CostDashboard] Error updating status:', error);
        }
    };

    if (!status) {
        return (
            <div className="bg-white rounded-lg shadow p-4 mb-4">
                <div className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                    <div className="h-8 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded"></div>
                </div>
            </div>
        );
    }

    const getStatusColor = (): string => {
        if (status.status === 'over') return 'bg-red-500';
        if (status.status === 'critical') return 'bg-orange-500';
        if (status.status === 'warning') return 'bg-yellow-500';
        return 'bg-green-500';
    };

    const getStatusIcon = (): string => {
        if (status.status === 'over') return '🛑';
        if (status.status === 'critical') return '⚠️';
        if (status.status === 'warning') return '⚡';
        return '✅';
    };

    const getStatusText = (): string => {
        if (status.status === 'over') return 'Budget Exceeded';
        if (status.status === 'critical') return 'Critical';
        if (status.status === 'warning') return 'Warning';
        return 'Good';
    };

    return (
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 mb-4">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-800 flex items-center">
                    💰 Budget Tracking
                </h3>
                <span className={`px-3 py-1 rounded-full text-sm font-semibold text-white ${status.status === 'over' ? 'bg-red-500' :
                    status.status === 'critical' ? 'bg-orange-500' :
                        status.status === 'warning' ? 'bg-yellow-500' :
                            'bg-green-500'
                    }`}>
                    {getStatusIcon()} {getStatusText()}
                </span>
            </div>

            {/* Current Cost */}
            <div className="mb-4">
                <div className="flex items-baseline justify-between mb-1">
                    <span className="text-gray-600">Current Cost:</span>
                    <span className="text-3xl font-bold text-gray-900">
                        ${status.currentCost.toFixed(2)}
                    </span>
                </div>
                <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>Budget: ${status.budget.toFixed(2)}</span>
                    <span>Target: ${status.targetCost.toFixed(2)}</span>
                </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
                <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Progress</span>
                    <span className="font-semibold">{status.percentUsed.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-6 relative overflow-hidden">
                    <div
                        className={`h-6 rounded-full transition-all duration-500 ${getStatusColor()}`}
                        style={{ width: `${Math.min(status.percentUsed, 100)}%` }}
                    />
                    {/* Target line */}
                    <div
                        className="absolute top-0 h-6 w-0.5 bg-gray-700"
                        style={{ left: `${(status.targetCost / status.budget) * 100}%` }}
                        title={`Target: $${status.targetCost.toFixed(2)}`}
                    />
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>$0</span>
                    <span>Target (80%)</span>
                    <span>${status.budget.toFixed(2)}</span>
                </div>
            </div>

            {/* Remaining Budget */}
            <div className="flex items-center justify-between py-3 border-t border-gray-200">
                <span className="text-gray-600 font-medium">Remaining:</span>
                <span className={`text-xl font-bold ${status.remaining < 0 ? 'text-red-600' :
                    status.remaining < status.budget * 0.1 ? 'text-orange-600' :
                        'text-green-600'
                    }`}>
                    ${status.remaining.toFixed(2)}
                </span>
            </div>

            {/* Cost Breakdown */}
            <div className="mt-4 pt-4 border-t border-gray-200">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Breakdown:</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex justify-between">
                        <span className="text-gray-600">Parts:</span>
                        <span className="font-medium">${status.breakdown.parts.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-600">Optimizations:</span>
                        <span className="font-medium">${status.breakdown.optimizations.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-600">Evolutions:</span>
                        <span className="font-medium">${status.breakdown.evolutions.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-600">Other:</span>
                        <span className="font-medium">${status.breakdown.other.toFixed(2)}</span>
                    </div>
                </div>
            </div>

            {/* Warnings */}
            {status.status === 'critical' && (
                <div className="mt-4 p-3 bg-orange-100 text-orange-800 rounded-lg text-sm flex items-start">
                    <span className="text-xl mr-2">⚠️</span>
                    <div>
                        <strong>WARNING:</strong> Approaching budget limit! Only ${status.remaining.toFixed(2)} remaining.
                        {status.recentWarnings?.length > 0 && (
                            <div className="mt-1 text-xs">
                                {status.recentWarnings[status.recentWarnings.length - 1].recommendation}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {status.status === 'over' && (
                <div className="mt-4 p-3 bg-red-100 text-red-800 rounded-lg text-sm flex items-start">
                    <span className="text-xl mr-2">🛑</span>
                    <div>
                        <strong>CRITICAL:</strong> Budget exceeded by ${Math.abs(status.remaining).toFixed(2)}!
                        No further costs can be added until budget is adjusted or costs are reduced.
                    </div>
                </div>
            )}

            {status.status === 'warning' && (
                <div className="mt-4 p-3 bg-yellow-100 text-yellow-800 rounded-lg text-sm flex items-start">
                    <span className="text-xl mr-2">⚡</span>
                    <div>
                        <strong>INFO:</strong> Cost above target (${status.currentCost.toFixed(2)} / ${status.targetCost.toFixed(2)} target).
                        Consider cost optimization strategies.
                    </div>
                </div>
            )}
        </div>
    );
};
